/**
 * Phase v3.4.1 — <CurrencyInput> primitive
 * =============================================================================
 * A drop-in wrapper around <Input> that renders a leading currency symbol
 * (₹, $, €, AED, USD, INR, …) inside the input with padding that adapts to
 * the symbol width. This exists to eliminate the recurring "AED clips over
 * the value" bug caused by hardcoded pl-11 in ~15 different call sites.
 *
 * Historically, callers wrote:
 *
 *   <div className="relative">
 *     <span className="absolute left-3 top-1/2 -translate-y-1/2 …">
 *       {currencySymbol(currency)}
 *     </span>
 *     <Input className="pl-11 tabular-nums" … />
 *   </div>
 *
 * That works fine for 1-char symbols (`₹`, `$`) but 3-char codes like `AED `,
 * `USD `, `SAR ` overflow the 44px padding and land on top of the digits.
 * Fix: compute the pad-left from the actual symbol length. Same width the
 * <span> is rendering plus a 12px gap.
 *
 * Usage:
 *   <CurrencyInput
 *     currency={currency}                       // ISO code, e.g. 'AED'
 *     value={form.amount}
 *     onChange={(e) => setForm({...form, amount: e.target.value})}
 *     placeholder="0"
 *     data-testid="txn-amount-input"
 *   />
 *
 * Optional overrides:
 *   symbol         — supply the already-resolved glyph if you have it.
 *   className      — merged into the <Input> (defaults to rounded-xl tabular-nums).
 *   symbolClassName — merged into the <span>. Rare, used to tweak size.
 */
import * as React from 'react';
import { Input } from './input';
import { cn } from '@/lib/utils';
import { currencySymbol } from '@/lib/format';

const CurrencyInput = React.forwardRef(function CurrencyInput(
  {
    currency,
    symbol: symbolProp,
    className,
    symbolClassName,
    type = 'number',
    inputMode = 'decimal',
    step = '0.01',
    style,
    ...rest
  },
  ref,
) {
  // Prefer explicit symbol prop, else resolve from currency code. Fall back
  // to the raw code so we always have something to render (never blank).
  const raw = symbolProp ?? (currency ? currencySymbol(currency) : '');
  // Trim trailing space that CURRENCIES table sometimes attaches (e.g. "AED ").
  const sym = String(raw || '').trim() || currency || '';

  // Padding math: 12px gutter on the left for the span + ~0.55rem per glyph
  // + 8px breathing room before the numeric value. This mirrors the working
  // formula already proven in TxnDrawer.jsx and produces:
  //   1 char  (₹, $)  → ~1.85rem
  //   2 char  (C$)    → ~2.40rem
  //   3 char  (AED)   → ~2.95rem
  const padLeftRem = 0.75 + Math.max(1, sym.length) * 0.55 + 0.35;

  return (
    <div className="relative">
      <span
        aria-hidden="true"
        className={cn(
          'absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none select-none',
          symbolClassName,
        )}
        data-testid={rest['data-testid'] ? `${rest['data-testid']}-symbol` : undefined}
      >
        {sym}
      </span>
      <Input
        ref={ref}
        type={type}
        inputMode={inputMode}
        step={step}
        className={cn('rounded-xl tabular-nums', className)}
        style={{ paddingLeft: `${padLeftRem}rem`, ...style }}
        {...rest}
      />
    </div>
  );
});

export { CurrencyInput };
export default CurrencyInput;
